import { CouponItemType, SupportedCurrencies } from '@monorepo/types';
import { getCurrencySign } from './cart';

/**
 * Options for price formatting
 */
export interface PriceFormatOptions {
  currency: SupportedCurrencies;
  currencyRates?: Record<string, number>;
  decimalPlaces?: number;
}

/**
 * Calculate converted price using currency rates
 * @param basePrice - The base price in USD or original currency
 * @param currency - The target currency
 * @param currencyRates - Currency conversion rates
 * @returns Converted price
 */
export const calculateConvertedPrice = (
  basePrice: number,
  currency: SupportedCurrencies,
  currencyRates?: Record<string, number>,
): number => {
  if (!basePrice) return 0;

  // If no currency rates provided or currency not found, return base price
  const rate = currencyRates?.[currency] ?? 1;
  return basePrice * rate;
};

/**
 * Format price with specified decimal places
 * @param price - The price to format
 * @param decimalPlaces - Number of decimal places (default: 2)
 * @returns Formatted price string
 */
export const formatPriceNumber = (price: number, decimalPlaces = 2): string => {
  return Number(price).toFixed(decimalPlaces);
};

/**
 * Format price with 2 decimal places and thousand separators
 * @param price - The price to format
 * @returns Formatted price string with thousand separators (e.g., "2,345.32")
 */
export const formatPriceWithThousandSeparators = (price: number): string => {
  const formatted = Number(price).toFixed(2);
  const [integerPart, decimalPart] = formatted.split('.');

  // Add thousand separators to integer part
  const integerWithSeparators = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ',',
  );

  return `${integerWithSeparators}.${decimalPart}`;
};

/**
 * Get formatted price string with currency symbol
 * @param basePrice - The base price to convert and format
 * @param options - Formatting options
 * @returns Formatted price string with currency symbol (e.g., "$100.00")
 */
export const getFormattedPriceWithCurrency = (
  basePrice: number,
  options: PriceFormatOptions,
): string => {
  const { currency, currencyRates, decimalPlaces = 2 } = options;

  const convertedPrice = calculateConvertedPrice(
    basePrice,
    currency,
    currencyRates,
  );
  const currencySign = getCurrencySign(currency);
  const formattedPrice = formatPriceNumber(convertedPrice, decimalPlaces);

  return `${currencySign}${formattedPrice}`;
};

/**
 * Main utility function for price formatting with currency conversion
 * @param basePrice - The base price to convert and format
 * @param options - Formatting options
 * @returns Formatted price string with currency symbol
 */
export const formatPrice = (
  basePrice: number,
  options: PriceFormatOptions,
): string => {
  return getFormattedPriceWithCurrency(basePrice, options);
};

/**
 * Format price for add-to-cart style display
 * @param basePrice - The base price (program price)
 * @param currency - The selected currency
 * @param currencyRates - Currency conversion rates
 * @returns Formatted price string matching add-to-cart style
 */

export const formatPriceWithCurrencyAndSign = (
  basePrice: number | undefined,
  currency: SupportedCurrencies,
  currencyRates?: Record<string, number>,
): string => {
  if (!basePrice) return getCurrencySign(currency) + '0.00';

  return formatPrice(basePrice, {
    currency,
    currencyRates,
    decimalPlaces: 2,
  });
};

export const getPriceAfterDiscount = (
  price: number,
  discountPercentage: number,
) => price * ((100 - discountPercentage) / 100);

export const getCouponDiscount = (
  couponType: CouponItemType,
  couponAmount: number,
  currency: SupportedCurrencies,
  currencyRates?: Record<string, number>,
): string => {
  switch (couponType) {
    case CouponItemType.Percentage:
      return `${couponAmount}%`;
    case CouponItemType.Nominal:
      return formatPriceWithCurrencyAndSign(
        couponAmount,
        currency,
        currencyRates,
      );
    default:
      return '';
  }
};
